import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {navigator} from 'lit-element-router';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {when} from 'lit/directives/when.js';
import {msg} from '@lit/localize';
import {ExplainQuestionSubject, ExplainQuestionType} from '@viamedici-spc/configurator-ts';

import store from '../../store';
import {setDownloadPanelSpecsheetActive, setExplainActive} from '../../slices/statusSlice';
import {downloadPDF, downloadPNG} from '../../slices/downloadSlice';

import {
	selectSpcIsSatisfied,
	selectLdt1,
	selectMountingInstruction,
	selectSpcSession,
	selectSpcConfiguration,
	selectBasicSpcAttributes,
	selectDetailSpcAttributes,
	selectBasicSpcAttributesSatisfied,
	selectRelux,
	selectDetailSpcAttributesSatisfactionPercentage,
	selectBasicSpcAttributesSatisfactionPercentage,
	selectDetailSpcAttributesSatisfied, selectFamily, selectFamilyConfig
} from '../../selectors';
import {routeUrl} from '../../util/routeUrl';
import next from '../../../icons/next';

@customElement('download-panel')
export class DownloadPanel extends connect(store)(navigator(LitElement)) {

	static styles = css`
		:host {
			--fix-color-black: #000;
			--fix-color-white: #fff;
			--fix-color-border: #606060;
			--fix-color-text: #fff;
			--fix-color-disabled-text: #939393;
			--fix-color-complete: #fff;
			--fix-color-incomplete: #606060;
			--fix-color-button-back: #252525;
			--fix-color-button-back-hover: #353535;
			--fix-color-selected: #606060;

			width: 100%;
			display: flex;
			flex-direction: column;
		}

		.downloads {
			padding: 60px 0 var(--small-padding) 0;
			background-color: var(--fix-color-black);
			display: flex;
			flex-direction: column;
			gap: 0;
			z-index: 20;
		}

		.title {
			display: flex;
			flex-direction: row;
			gap: var(--small-padding);
			color: var(--fix-color-white);
			padding: var(--small-padding) var(--large-padding);
			justify-content: center;
			align-items: end;
		}
		@media (max-width: 1024px) {
			.title {
				flex-direction: column;
				align-items: center;
				padding: var(--small-padding) var(--small-padding);
			}
		}

		.sectionTitle {
			font: var(--font-downloads-title);
			color: var(--fix-color-white);
		}

		.sectionTitle a {
			text-decoration: none;
			color: var(--fix-color-white);
		}
		.familySelection {
			display: flex;
			flex-direction: row;
			gap: var(--small-padding);
		}

		.family {
			font: var(--font-downloads-title);
			color: var(--fix-color-white);
		}

		.panelLinks {
			display: flex;
			flex-direction: row;
			gap: var(--small-padding);
			text-align: right;
			line-height: 1.2;
			margin-left: auto;
		}
		@media (max-width: 1024px) {
			.panelLinks {
				margin-left: initial;
			}
		}

		.productInfoLink {
			font: var(--font-widget-big-label);
			color: var(--fix-color-white);
			text-decoration: none;
		}

		.downloadButtons {
			position: sticky;
			top: 0;
			background-color: var(--fix-color-black);
			display: flex;
			flex-direction: row;
			flex-grow: 1;
			margin-bottom: auto;
			padding: var(--small-padding) var(--large-padding);
			gap: var(--small-padding);
		}
		@media (max-width: 1024px) {
			.downloadButtons {
				flex-direction: column;
				padding: var(--small-padding) var(--small-padding);
			}
		}
		.downloadButton {
			text-align: left;
			font: var(--font-widget-label);
			color: var(--fix-color-white);
			display: flex;
			position: relative;
			flex-direction: row;
			gap: var(--padding);
			justify-content: space-between;
			padding: var(--very-small-padding);
			margin-bottom: 0;
			width: 100%;
			background-color: transparent;
			border: 1px solid var(--fix-color-border);
			cursor: pointer;
		}

		.downloadButton.all {
			color: var(--fix-color-white);
			border-color: var(--fix-color-border);
			background-color: var(--fix-color-button-back);
		}

		.specsheet {
			background-color: #ffffff;
			display: none;
			flex-direction: row;
			overflow: hidden;
			gap: 20px;
			position: fixed;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			width: 100%;
			height: 100vh;
			z-index: 800;
			padding: 50px 20px 0 20px;
			max-height: initial;
			box-sizing: border-box;
			align-items: start;
		}

		.specsheet.specsheetExpanded {
			display: flex;
		}


		.specsheetActions {
			padding: var(--very-small-padding) 0;
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			gap: var(--padding);
			flex-basis: 20%;
		}

		.pages {
			max-height: 100vh;
			container-type: inline-size;
			margin: 0 auto 0 0;
			flex-basis: 80%;
			overflow-y: scroll;
		}


		.downloadPDF {
			font: var(--font-widget-small-title);
			display: flex;
			padding: 10px;
			border: 1px solid #a5a5a5;
			color: #fff;
			background-color: #000;
		}

		.closeButton {
			cursor: pointer;
			background: transparent;
			border: none;
			margin-left: auto;
			width: 30px;
			color: #000;
		}

		.closeButton svg {
			width: 100%;
		}

		.toggle {
			width: 100%;
			color: white;
			border: none;
			background-color: transparent;
			cursor: pointer;
			padding: 10px 0;
			display: block;
			text-align: center;
		}

		.toggleIcon {
			display: block;
			transition: transform ease-in-out 0.4s;
			transform: rotate(180deg);
		}

		.downloadItems {
			display: flex;
			flex-direction: row;
			flex-grow: 1;
			margin-bottom: auto;
			padding: 0 var(--large-padding);
			gap: var(--large-padding);
		}

		.downloadItem {
			display: flex;
			flex-basis: 20%;
			font: var(--font-widget-title);
			padding-bottom: 12px;
			color: #ffffff;
			flex-grow: 1;
			flex-direction: column;
		}


		.planningData {
			display: grid;
			grid-template-rows: 1fr 1fr;
			grid-template-columns: repeat(2, 1fr);
			gap: var(--very-small-padding) var(--small-padding);
		}

		.planningData *:first-child {
			grid-column: 1 / 3;
			grid-row: 1 / 1;
		}


		.downloadButton.half {
			width: 50%;
		}


		.icon {
			display: inline-block;
			height: 16px;
			width: 16px;
			margin-right: 10px;
			color: #fff;
		}

		.icon.state {
			color: var(--color-warn);
		}

		.icon.state.satisfied {
			color: var(--color-ok);
		}


		.icon svg {
			width: 100%;
			height: auto;
		}

		.icon svg * {
			stroke: currentColor !important;
		}

		@media (max-width: 1024px) {
			.downloads {
				padding: 100px var(--small-padding) var(--small-padding) var(--small-padding);
			}

			.downloadItems {
				min-width: 320px;
				flex-direction: column;
				padding: 0 var(--small-padding);
				gap: 0;
			}

			.specsheet {
				flex-direction: column;
			}

			.pages {
				order: 2;
				width: 100%;
				flex-basis: 100%;
			}

			.specsheetActions {
				order: 1;
				flex-basis: initial;
			}
		}
	`;

	@property({type: Boolean})
	active = false;

	@property({type: Boolean})
	specsheet = false;

	@property({type: String})
	specsheetType = 'family';

	constructor() {
		super();
		this.spcWasNotSatisfiedYet = true;
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.lang = state.status.lang;
		this.region = state.status.region;
		this.host = state.status.host;
		this.base = state.status.baseUrl;

		this.downloadStatus = state.download.status;
		this.downloadPngStatus = state.download.statusPng;
		this.downloadPanelSpecsheetActive = this.status.downloadPanelSpecsheetActive;
		this.specsheetType = state.status.specsheetType;

		this.relux = selectRelux(state);
		this.spcIsSatisfied = selectSpcIsSatisfied(state);
		this.session = selectSpcSession(state);
		this.spcConfiguration = selectSpcConfiguration(state);
		this.basicSpcAttributes = selectBasicSpcAttributes(state);
		this.isBasicSatisfied = selectBasicSpcAttributesSatisfied(state);
		this.isDetailSatisfied = selectDetailSpcAttributesSatisfied(state);
		this.detailSpcAttributes = selectDetailSpcAttributes(state);
		this.basicPercentage = selectBasicSpcAttributesSatisfactionPercentage(state);
		this.detailPercentage = selectDetailSpcAttributesSatisfactionPercentage(state);
		this.fullPercentage = Math.ceil((this.basicPercentage + this.detailPercentage) / 2);
		this.family = selectFamily(state);
		this.selectedFamilyConfig = selectFamilyConfig(state);

		if (this.spcIsSatisfied && this.spcWasNotSatisfiedYet) {
			this.spcWasNotSatisfiedYet = false;
			this.showSpecsheet('detail');
		}

		if (this.downloadPanelSpecsheetActive) {
			document.body.classList.remove('slx-body--colorSchemeInverted');
		} else {
			document.body.classList.add('slx-body--colorSchemeInverted');
		}
	}

	connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
	}

	handleKeydown = (e) => {
		switch (e.keyCode) {
			case 27: // ESC
				if (this.downloadPanelSpecsheetActive) {
					this.close();
				}
				break;
		}
	};


	downloadPDF(e) {
		store.dispatch(downloadPDF({type: this.specsheetType}));
	}

	downloadPNG(e) {
		store.dispatch(downloadPNG());
	}

	editConfiguration(e) {
		this.closeSpecsheet();
	}

	startNewConfiguration(e) {
		// this.navigate(routeUrl({media: 'sections', key: 'new'}));
		window.location.reload();
	}

	copyTendertext(e) {
		const event = new CustomEvent('copyToClipboard', {
			bubbles: true,
			composed: true
		});
		this.dispatchEvent(event);
		this.tenderTextCopied = true;
	}

	showSpecsheet(type) {
		console.log('showSpecsheet', type, this.spcIsSatisfied, this.spcIsSatisfied, this.isDetailSatisfied);
		if (!this.spcIsSatisfied && type === 'detail') {
			// this.explainNotSatisfied();
		} else {
			store.dispatch(setDownloadPanelSpecsheetActive(
				{downloadPanelSpecsheetActive: true, specsheetType: type}
			));
		}
	}

	explainNotSatisfied() {
		this.session.explain({
			question: ExplainQuestionType.whyIsNotSatisfied,
			subject: ExplainQuestionSubject.configuration
		}, 'full').then(
			explainAnswer => {
				let causedBy = null;
				let causedByRules = null;
				let solution = null;
				if (explainAnswer.constraintExplanations.length > 0) {
					causedBy = explainAnswer.constraintExplanations[0].causedByCardinalities.map(cbc => (
						{
							attributeLocalId: cbc.localId,
						}
					));
					causedByRules = explainAnswer.constraintExplanations[0].causedByRules.map(cbr => (
						{
							name: cbr.localId,
						}
					));
					solution = null;
					store.dispatch(setExplainActive(
						{
							explainActive: true,
							desire: null,
							causedBy: causedBy,
							causedByRules: causedByRules,
							solution: solution
						}
					));
				}
			}
		);
	}

	hideSpecsheet(type) {
		store.dispatch(setDownloadPanelSpecsheetActive({downloadPanelSpecsheetActive: false, type: null}));
	}
	close() {
		store.dispatch(setDownloadPanelSpecsheetActive({downloadPanelSpecsheetActive: false, type: null}));
	}

	download(url) {
		console.log('download', url);
		window.open(url);
	}
	reluxUrl(type) {
		return this.relux?.find(r => r.type === type)?.url;
	}
	productInfoUrl() {
		return 'https://www.selux.com/int/' + this.lang + this.selectedFamilyConfig?.productInfoUrl;
	}
	accessoryUrl() {
		return this.selectedFamilyConfig?.accessoryUrl ? 'https://www.selux.com/int/' + this.lang + this.selectedFamilyConfig?.accessoryUrl : null;
	}
	configuratorUrl() {
		return this.host + this.base + '/' + this.region + '/' + this.lang + '/' + this.selectedFamilyConfig?.configurator;
	}


	render() {
		let url;
		if (this.host === 'https://selux.com' || this.host === 'https://www.selux.cdlx.dev') {
			url = `/${this.region}/${this.lang}/datahub`;
		} else {
			url = '/demo.php';
		}

		return html`
            <div class="downloads ${classMap({specsheetExpanded: this.downloadPanelSpecsheetActive})}">
				<div class="title">
					<div class="familySelection">
						<div class="sectionTitle"><a href="${url}"><strong>${msg('Data Hub')}</strong></a></div>
						<div class="family">
							<family-selector></family-selector>
						</div>
					</div>
					${when(this.family && this.family !== 'default',
						() => html`
							<div class="panelLinks">
								<a href="${this.productInfoUrl()}" class="productInfoLink">
									${msg('Learn more about')} ${this.selectedFamilyConfig?.label?.[this.lang]}
									${next}
								</a>
								${when(this.accessoryUrl(), () => html`
									<br><a href="${this.accessoryUrl()}" class="productInfoLink">
										${this.selectedFamilyConfig?.label?.[this.lang]} ${msg('Accessory')}
										${next}
									</a>
								`)}
								${when(this.selectedFamilyConfig?.configurator, () => html`
									<br><a target="_blank" href="${this.configuratorUrl()}" class="productInfoLink">${msg('Configure')}
										${this.selectedFamilyConfig?.label?.[this.lang]}
										${next}
									</a>
								`)}
							</div>
						`,
						() => html`
							<div></div>
						`)}
				</div>
				${when(!this.selectedFamilyConfig?.hideDownloadButtons, () => html`
					<div class="downloadButtons">
						<download-button
							.action=${(e) => this.showSpecsheet('family')}
							label="${msg('Basic data')}"
							.infos=${[
								msg('model overview, product and project images') //Modellübersicht, Produkt- und Projektbilder
							]}
							specsheetType="family"
							?disabled=${false}>
						</download-button>
						<download-button
							class="basicButton"
							.action=${(e) => this.showSpecsheet('basic')}
							label="${msg('Basic specsheet')}"
							.infos=${[
								msg('basic data sheet, product and project images'), // Entwurfsdatenblatt, Produkt- und Projektbilder
								msg('photometry, revit, assembly instructions') // Photometrien, Revit, Montageanleitung
							]}
							specsheetType="basic"
							?disabled=${!this.isBasicSatisfied}>
						</download-button>
						<download-button
							class="detailButton"
							.action=${(e) => this.showSpecsheet('detail')}
							label="${msg('Detailed specsheet')}"
							.infos=${[
								msg('detailed data sheet, product and project images'), // Detaildatenblatt, Produkt- und Projektbilder
								msg('photometry, revit, assembly instructions'),
								msg('tender texts') // Ausschreibungstexte
							]}
							specsheetType="detail"
							?disabled=${!this.spcIsSatisfied}>
						</download-button>
					</div>
				`)}
            </div>
			${when(!this.family || this.family === 'default', () => html`
				<family-list></family-list>
			`)}
			<div class="specsheet ${classMap({specsheetExpanded: this.downloadPanelSpecsheetActive})}">
				<download-sidebar></download-sidebar>
				<div class="pages">
					${when(this.downloadPanelSpecsheetActive, () => html`<shared-specsheet type="${this.specsheetType}"></shared-specsheet>`)}
				</div>
            </div>
        `;
	}
}
